import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BoutonPrincipal } from '../ui/Primitives';
import { ChampRecherchable } from '../ui/ChampRecherchable';
import { ModaleOrganisation } from '../repertoire/ModaleOrganisation';
import { ErreurApi } from '../../lib/api';
import { useCreerEntreprise, useEntreprises } from '../../lib/useEntreprises';
import { useSites } from '../../lib/useSites';
import type { EntreprisePositionnable, SaisiePositionnement } from '../../lib/useMatching';

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-normal text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function Libelle({ texte, children }: { texte: string; children: ReactNode }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {texte}
      </span>
      {children}
    </label>
  );
}

function ChoixImmersion({
  avecImmersion,
  onChanger,
  desactive,
}: {
  avecImmersion: boolean;
  onChanger: (avecImmersion: boolean) => void;
  desactive: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        Immersion
      </span>
      <div
        role="radiogroup"
        aria-label="Période d’immersion"
        className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white p-1"
      >
        {[true, false].map((valeur) => (
          <button
            key={String(valeur)}
            type="button"
            role="radio"
            aria-checked={avecImmersion === valeur}
            disabled={desactive}
            onClick={() => onChanger(valeur)}
            className={`min-h-[36px] flex-1 rounded-lg px-4 text-xs font-bold transition disabled:cursor-not-allowed [@media(pointer:coarse)]:min-h-[40px] ${
              avecImmersion === valeur
                ? 'bg-codialis-navy text-white'
                : 'text-codialis-gray hover:bg-gray-50'
            }`}
          >
            {valeur ? 'Oui' : 'Non'}
          </button>
        ))}
      </div>
    </div>
  );
}

/** SIRET lisible, groupé comme sur un Kbis : 123 456 789 00012. */
function siretLisible(siret: string): string {
  const chiffres = siret.replace(/\s/g, '');
  return chiffres.length === 14
    ? `${chiffres.slice(0, 3)} ${chiffres.slice(3, 6)} ${chiffres.slice(6, 9)} ${chiffres.slice(9)}`
    : siret;
}

/**
 * Création de l'employeur qui manque, sans quitter le positionnement.
 *
 * La même fiche que partout (`ModaleOrganisation`), en nature entreprise
 * imposée et rattachée d'office à l'antenne du candidat : sans ce
 * rattachement, l'entreprise créée ne serait pas positionnable ici. Les listes
 * suggérées de la fiche viennent de l'API des entreprises, chargée seulement à
 * l'ouverture.
 */
function CreationEntreprise({
  site,
  onFermer,
  onCreee,
}: {
  site: string;
  onFermer: () => void;
  onCreee: (entrepriseId: string) => void;
}): JSX.Element {
  const client = useQueryClient();
  const referentiels = useEntreprises({ site });
  const { data: sites } = useSites();
  const creer = useCreerEntreprise();
  const [horsAntenne, setHorsAntenne] = useState(false);
  const donnees = referentiels.data;

  return (
    <ModaleOrganisation
      ouverte
      natureImposee="ENTREPRISE"
      statutsRelation={donnees?.statuts ?? []}
      typesPartenaire={[]}
      secteurs={donnees?.secteursSuggeres ?? []}
      typesEmployeur={donnees?.typesEmployeurSuggeres ?? []}
      employeursSpecifiques={donnees?.employeursSpecifiquesSuggeres ?? []}
      sitesDisponibles={(sites?.sites ?? []).map((entree) => entree.code)}
      sitesParDefaut={[site]}
      enCours={creer.isPending}
      erreur={
        horsAntenne
          ? `Rattachez l’entreprise à l’antenne ${site} : c’est celle du candidat.`
          : creer.error instanceof ErreurApi
            ? creer.error.message
            : null
      }
      onFermer={() => {
        creer.reset();
        onFermer();
      }}
      onEnregistrer={(sortie) => {
        if (!sortie.entreprise) return;
        if (!sortie.entreprise.sites.includes(site)) {
          setHorsAntenne(true);
          return;
        }
        setHorsAntenne(false);
        // `effectif` non saisi arrive à `null`, refusé à la création : on l'omet.
        const { effectif, ...entree } = sortie.entreprise;
        creer.mutate(
          { ...entree, ...(effectif === null ? {} : { effectif }) },
          {
            onSuccess: (creee) => {
              void client.invalidateQueries({ queryKey: ['positionnements-candidat'] });
              onCreee(creee.id);
            },
          },
        );
      }}
    />
  );
}

/**
 * Positionnement d'un candidat en entreprise, avec ou sans immersion — le même
 * formulaire au matching et dans la fiche d'entretien.
 *
 * Avec immersion, la période est obligatoire et le contrat attendra le dernier
 * jour. Sans immersion, le positionnement part tout de suite au pôle
 * administratif. Les règles sont tenues par l'API ; l'écran ne fait qu'éviter
 * un envoi évidemment incomplet.
 */
export function FormulairePositionnement({
  entreprises,
  creationEntreprise,
  enCours,
  onValider,
  children,
}: {
  /** Fourni quand l'entreprise reste à choisir ; absent quand elle est donnée. */
  entreprises?: EntreprisePositionnable[];
  /**
   * Antenne du candidat, quand la personne peut créer l'employeur qui manque
   * (`com-entreprises`). Absent : la recherche ne propose que l'existant.
   */
  creationEntreprise?: { site: string };
  enCours: boolean;
  onValider: (saisie: SaisiePositionnement & { entrepriseId?: string }) => void;
  children?: ReactNode;
}): JSX.Element {
  const [entrepriseId, setEntrepriseId] = useState('');
  const [creation, setCreation] = useState(false);
  const [avecImmersion, setAvecImmersion] = useState(true);
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');

  const entrepriseManquante = entreprises !== undefined && entrepriseId === '';
  const periodeIncomplete = avecImmersion && (!debut || !fin || fin < debut);

  const valider = (): void =>
    onValider({
      ...(entreprises ? { entrepriseId } : {}),
      immersion: avecImmersion,
      ...(avecImmersion ? { dateDebut: debut, dateFin: fin } : {}),
    });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap">
        {entreprises && (
          <div className="sm:col-span-2 lg:min-w-[280px] lg:flex-1">
            {/* Recherche par raison sociale, SIRET ou ville : un employeur se
                retrouve par son SIRET quand deux enseignes se ressemblent. */}
            <ChampRecherchable
              id="positionnement-entreprise"
              libelle="Entreprise"
              placeholder="Raison sociale, SIRET ou ville…"
              options={entreprises.map((entreprise) => ({
                id: entreprise.id,
                libelle: entreprise.denomination,
                detail: [
                  `SIRET ${siretLisible(entreprise.siret)}`,
                  [entreprise.codePostal, entreprise.ville].filter(Boolean).join(' '),
                ]
                  .filter(Boolean)
                  .join(' · '),
                motsCles: entreprise.siret,
              }))}
              valeur={entrepriseId || null}
              onChoisir={setEntrepriseId}
              {...(creationEntreprise
                ? {
                    creation: {
                      libelle: 'Créer une nouvelle entreprise',
                      onCreer: () => setCreation(true),
                    },
                  }
                : {})}
              aucunResultat="Aucune entreprise de l’antenne ne correspond."
              desactive={enCours}
              classeLibelle="text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
            />
          </div>
        )}
        <div className="sm:col-span-2 lg:min-w-[180px]">
          <ChoixImmersion
            avecImmersion={avecImmersion}
            onChanger={setAvecImmersion}
            desactive={enCours}
          />
        </div>
        {avecImmersion && (
          <>
            <Libelle texte="Début">
              <input
                type="date"
                value={debut}
                onChange={(evenement) => setDebut(evenement.target.value)}
                disabled={enCours}
                className={CLASSE_CHAMP}
              />
            </Libelle>
            <Libelle texte="Fin">
              <input
                type="date"
                value={fin}
                min={debut || undefined}
                onChange={(evenement) => setFin(evenement.target.value)}
                disabled={enCours}
                className={CLASSE_CHAMP}
              />
            </Libelle>
          </>
        )}
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-1">
          <BoutonPrincipal
            onClick={valider}
            disabled={enCours || entrepriseManquante || periodeIncomplete}
          >
            {avecImmersion ? 'Positionner' : 'Positionner sans immersion'}
          </BoutonPrincipal>
          {children}
        </div>
      </div>
      {creation && creationEntreprise ? (
        <CreationEntreprise
          site={creationEntreprise.site}
          onFermer={() => setCreation(false)}
          onCreee={(id) => {
            setEntrepriseId(id);
            setCreation(false);
          }}
        />
      ) : null}
      <p className="text-[11px] text-codialis-gray">
        {avecImmersion
          ? 'Le contrat attendra le dernier jour de l’immersion. Sa fin reste modifiable.'
          : 'Le pôle administratif est prévenu tout de suite pour établir les documents contractuels.'}
      </p>
    </div>
  );
}
